import { Injectable } from '@angular/core';
import { TradingMarketConfiguration } from '@models/trading-market-configuration.class';
import { UnparsedUserAppConfiguration } from '@models/user-app-config.class';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { AppState } from 'src/app';
import { ApiGatewayService } from 'src/shared';

@Injectable()
export class AppManagementService {
  private appConfiguration$: Observable<any>;
  private gbmAPI: string;
  private gbmAPI$: Observable<string>;

  constructor(
    private store: Store<AppState>,
    private apiGatewayService: ApiGatewayService,
  ) {
    this.appConfiguration$ = this.store.select('config');

    this.gbmAPI$ = this.appConfiguration$.pipe(
      filter((mapShadowed: any) => Map.isMap(mapShadowed)),
      map((mapShadowed: any) => mapShadowed.get('api')),
      take(1),
    );

    this.gbmAPI$.subscribe((api: string) => (this.gbmAPI = api));
  }

  public getSolaceDataTopic(isSIC: boolean): Observable<any> {
    const data: any = {
      isRealTime: true,
      issic: !!isSIC,
      instruments: ['*'],
    };

    return this.apiGatewayService.post(
      `${this.gbmAPI}api/AppManagement/GetSolaceDataTopic`,
      data,
    );
  }

  public getSolaceLTopic(isSIC: boolean, hasLevel2: boolean): Observable<any> {
    const data: any = {
      isRealTime: true,
      issic: !!isSIC,
      instruments: ['*'],
    };

    return this.apiGatewayService.post(
      `${this.gbmAPI}api/AppManagement/GetSolaceLTopic/${String(hasLevel2)}`,
      data,
    );
  }

  public getSolaceIndexesTopic(): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/AppManagement/GetSolaceIndexesTopic`,
    );
  }

  public getCapitalMarketOperationTime(): Observable<any> {
    return this.apiGatewayService.get(
      `${this.gbmAPI}api/AppManagement/GetCapitalMarketOperationTime`,
    );
  }

  public getMarketChartConfiguration(): Observable<any> {
    return this.apiGatewayService.get(
      `${this.gbmAPI}api/AppManagement/GetMarketChartConfiguration`,
    );
  }

  public addMarketChartConfiguration(
    name: string,
    configuration: any,
  ): Observable<any> {
    const data: any = {
      description: name,
      configuration,
    };

    return this.apiGatewayService.post(
      `${this.gbmAPI}api/AppManagement/AddMarketChartConfiguration`,
      data,
    );
  }

  public updateMarketChartConfiguration(data: any): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/AppManagement/UpdateMarketChartConfiguration`,
      data,
    );
  }

  public deleteMarketChartConfiguration(chartId: number): Observable<any> {
    const data: any = {
      request: chartId,
    };

    return this.apiGatewayService.post(
      `${this.gbmAPI}api/AppManagement/DeleteMarketChartConfiguration`,
      data,
    );
  }

  /**
   * Get User Application Configuration
   */
  public getUserAppConfiguration(): Observable<UnparsedUserAppConfiguration> {
    return this.gbmAPI$.pipe(
      switchMap((gbmAPI) =>
        this.apiGatewayService.get(
          `${gbmAPI}api/AppManagement/GetUserAppConfiguration`,
        ),
      ),
    ) as Observable<UnparsedUserAppConfiguration>;
  }

  /**
   * Update User Application Configuration
   *
   * @param Object with user configuration
   */
  public updateUserAppConfiguration(
    data: UnparsedUserAppConfiguration,
  ): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/AppManagement/UpdateUserAppConfiguration`,
      data,
    );
  }

  public activateUser(data: any): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Security/ActivateUser`,
      data,
    );
  }

  /**
   * Get users role
   */
  public getUserRole(): Observable<any> {
    return this.apiGatewayService.get(`${this.gbmAPI}api/User/GetUserRoles`);
  }

  /**
   *
   */

  public getTradingMarketConfiguration(): Observable<TradingMarketConfiguration> {
    return this.apiGatewayService.get(
      `${this.gbmAPI}api/AppManagement/GetTradingMarketConfiguration`,
    );
  }
}
